import { Check, ClipboardText } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Button, Paper, Text } from "@/index";

export type ComponentExampleProps = {
  title: string;
  description?: string;
  code: string;
  children: ReactNode;
};

export function ComponentExample({ title, description, code, children }: ComponentExampleProps) {
  const [mode, setMode] = useState<"design" | "code">("design");
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    await window.navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <Paper padding="none" className="bfrs-overflow-hidden">
      <div className="bfrs-flex bfrs-flex-col bfrs-gap-3 bfrs-border-b bfrs-border-border bfrs-px-5 bfrs-py-4 sm:bfrs-flex-row sm:bfrs-items-start sm:bfrs-justify-between">
        <div>
          <Text variant="title">{title}</Text>
          {description && <Text variant="body-sm" tone="muted" className="bfrs-mt-1">{description}</Text>}
        </div>
        <div className="bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-2">
          <div className="bfrs-inline-flex bfrs-rounded-md bfrs-border bfrs-border-border bfrs-bg-surface-muted bfrs-p-1">
            <button
              type="button"
              className={`bfrs-rounded-sm bfrs-px-3 bfrs-py-1.5 bfrs-text-sm bfrs-font-semibold ${mode === "design" ? "bfrs-bg-surface-card bfrs-text-text-primary bfrs-shadow-low" : "bfrs-text-text-muted"}`}
              onClick={() => setMode("design")}
            >
              Design
            </button>
            <button
              type="button"
              className={`bfrs-rounded-sm bfrs-px-3 bfrs-py-1.5 bfrs-text-sm bfrs-font-semibold ${mode === "code" ? "bfrs-bg-surface-card bfrs-text-text-primary bfrs-shadow-low" : "bfrs-text-text-muted"}`}
              onClick={() => setMode("code")}
            >
              Code
            </button>
          </div>
          <Button variant="outline" size="sm" leftIcon={copied ? <Check size={14} /> : <ClipboardText size={14} />} onClick={copyCode}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>
      {mode === "design" ? (
        <div className="bfrs-min-h-36 bfrs-bg-surface-card bfrs-p-5">{children}</div>
      ) : (
        <pre className="bfrs-max-h-[520px] bfrs-overflow-auto bfrs-bg-surface-muted bfrs-p-5 bfrs-text-sm bfrs-leading-6 bfrs-text-text-secondary">
          <code>{code}</code>
        </pre>
      )}
    </Paper>
  );
}
